import React, { useEffect, useRef } from 'react';
import { App, Button } from 'antd';
import {
  ToolOutlined,
  StopOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';

const SUPPORT_EMAIL = 'support@brandrecallai.com';

/**
 * Detail shape dispatched by `apiClient.ts` for the structured "restricted
 * action" errors raised by the backend credit middleware:
 *   - `action_not_configured` (HTTP 500): action key missing in `action_pricing`.
 *   - `action_disabled`       (HTTP 403): action explicitly disabled.
 *
 * Both are surfaced to the user as a toast (rather than the existing modal
 * used for `insufficient_credits`) because the user cannot self-resolve the
 * problem in-app — the only useful next step is to contact support.
 */
interface ActionRestrictedDetail {
  kind: 'action_not_configured' | 'action_disabled';
  action?: string;
  title: string;
  message: string;
  rawMessage?: string;
  status?: number;
}

const formatActionName = (action?: string): string => {
  if (!action) return '';
  return action
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

/**
 * Global listener that turns the `action-restricted` custom event into a
 * polished Ant Design notification. Mounted once at the app shell level so
 * every page that goes through `apiClient` automatically gets the same UX.
 *
 * Returns `null` — it only registers side-effect listeners on `document`.
 */
const GlobalActionRestrictedFeedback: React.FC = () => {
  const { notification } = App.useApp();
  // Dedupe so a burst of identical errors (e.g. parallel calls from a page)
  // does not spam the corner of the screen with the same toast.
  const lastShownRef = useRef<{ key: string; at: number } | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<ActionRestrictedDetail>).detail;
      if (!detail || !detail.kind) return;

      const dedupeKey = `${detail.kind}::${detail.action || ''}`;
      const now = Date.now();
      if (
        lastShownRef.current &&
        lastShownRef.current.key === dedupeKey &&
        now - lastShownRef.current.at < 2000
      ) {
        return;
      }
      lastShownRef.current = { key: dedupeKey, at: now };

      const isNotConfigured = detail.kind === 'action_not_configured';
      const actionLabel = formatActionName(detail.action);
      const notificationKey = `action-restricted-${dedupeKey}`;

      notification.error({
        key: notificationKey,
        message: detail.title,
        description: (
          <div>
            <div className="text-sm leading-relaxed text-gray-700">
              {detail.message}
            </div>
            {actionLabel && (
              <div className="mt-2 text-xs text-gray-500">
                Feature: <span className="font-medium">{actionLabel}</span>
              </div>
            )}
          </div>
        ),
        icon: isNotConfigured ? (
          <ToolOutlined style={{ color: '#fa8c16' }} />
        ) : (
          <StopOutlined style={{ color: '#f5222d' }} />
        ),
        duration: 7,
        placement: 'topRight',
        btn: (
          <div className="flex gap-2">
            <Button
              size="small"
              icon={<CustomerServiceOutlined />}
              onClick={() => {
                notification.destroy(notificationKey);
                const subject = encodeURIComponent(
                  `Feature unavailable: ${actionLabel || detail.action || 'unknown action'}`
                );
                const body = encodeURIComponent(
                  `Hi BRAI Support,\n\nI ran into an "${detail.title}" error while using the app.\n\nFeature: ${actionLabel || detail.action || 'unknown'}\nReason: ${detail.kind}\n${detail.rawMessage ? `Backend message: ${detail.rawMessage}\n` : ''}\nPlease help me get this enabled.\n\nThanks!`
                );
                window.location.href = `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
              }}
            >
              Contact support
            </Button>
            <Button
              type="primary"
              size="small"
              onClick={() => notification.destroy(notificationKey)}
            >
              Got it
            </Button>
          </div>
        ),
      });
    };

    document.addEventListener('action-restricted', handler as EventListener);
    return () => {
      document.removeEventListener('action-restricted', handler as EventListener);
    };
  }, [notification]);

  return null;
};

export default GlobalActionRestrictedFeedback;
